import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Table,
  Button,
  Drawer,
  Tag,
  Tooltip,
  message,
  Modal,
  Progress,
  Empty,
  Checkbox,
  Badge,
} from "antd";
import StageHeader from "./page/StageHeader";
import PageFilter from "./page/PageFilter";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { apiConfig, apiTokenRequest } from "../authConfig";
import { openServerUrl, preopenWindow } from "../utils/apiError";
import { reportOpenFailure } from "../utils/openLinkFeedback";
import { useApp } from "../context/AppContext";
import { Role } from "../constants/roles";
import {
  STAGE_ORDER,
  STAGE_META,
  controlDocCode,
  progressPercentForStage,
  summarizeStageCounts,
  canRoleEditStageChecklist,
  isStageChecklistComplete,
  allItemsForStage,
} from "../utils/controlImplementation";
import { stripCompanyPrefixFromDocumentTitle } from "../utils/documentDisplayName";
import { resolveCompanyLogoUrl } from "../utils/persistableBlobUrl";
import { DelegateDocxEditorPanel } from "./DelegateCompanyArtefactWorkspace";
import { DOCX_PREVIEW_BODY_CLASS } from "./ArtefactTemplatePreview";
import { CollapsiblePane } from "./CollapsiblePane";
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";
import { listUnsetSoaControls, plainSoaGateMessage } from "../utils/soaApplicability";
import { SoaUnsetNotice } from "./SoaUnsetNotice";

/** Single source of truth for stage filter tabs at the top of the table. */
const STAGE_FILTER_CHIPS = [
  { key: "all", label: "All" },
  ...STAGE_ORDER.map((s) => ({ key: s, label: STAGE_META[s].label.toUpperCase() })),
];

const FAMILY_TAG_CLASS = {
  Organizational: "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-hero)] border-[color:var(--color-border)]",
  People: "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]",
  Physical: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]",
  Technological: "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]",
};

function formatRelativeDate(iso) {
  return iso ? formatDdMmYyyy(iso).replace("-", "") : "";
}

function activeDocsArray(pkg) {
  const docs = pkg?.docs && typeof pkg.docs === "object" ? pkg.docs : {};
  return Object.values(docs).filter((d) => d && !d.deactivatedAt);
}

function findAssigneeMember(projectMembers, role) {
  if (!Array.isArray(projectMembers)) return null;
  const wanted = role || "";
  return (
    projectMembers.find((m) => {
      const r = String(m.role || "").toUpperCase();
      const w = String(wanted || "").toUpperCase();
      if (!w) return false;
      return r === w || r === w.replace("_", "");
    }) || null
  );
}

/**
 * Workbench step 7 — Control Implementation.
 */
export function ControlImplementation({ project, user }) {
  const { instance } = useMsal();
  const { updateProjectProfile, saveScopingProfile, fetchProjectProfile } = useApp();
  const [busy, setBusy] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [drawerControlId, setDrawerControlId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const isLeadAdvisor = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADMIN;
  const isAdvisor = user?.role === Role.ADVISOR;
  const isSpoc = user?.role === Role.COMPANY_SPOC;
  const isDelegate = user?.role === Role.COMPANY_DELEGATE;

  const pkg = project?.profile?.controlImplementationPackage || null;
  const companyDisplayName = project?.companyName || project?.name || "";
  /**
   * Commandment VIII — backlog #39: mammoth (blob-docx-preview-html) does not render
   * DOCX headers/footers, so a header-placed client logo is invisible in this in-app
   * preview even though it renders correctly in the real downloaded artefact. Overlay
   * it client-side from the same Company.branding source every other preview already
   * uses (see persistableBlobUrl.js / MsaContractBody.jsx / GapAnalysis.jsx) — no new
   * fetch, no backend change, download path untouched.
   */
  const companyLogoUrl = resolveCompanyLogoUrl(project);
  const allDocs = useMemo(() => activeDocsArray(pkg), [pkg]);

  const docs = useMemo(() => {
    if (isDelegate) return allDocs.filter((d) => d.stage === "delegate_review");
    if (isSpoc) return allDocs.filter((d) => d.stage === "spoc_review" || d.stage === "published");
    return allDocs;
  }, [allDocs, isDelegate, isSpoc]);

  const summary = useMemo(
    () => summarizeStageCounts(pkg?.docs || {}),
    [pkg?.docs],
  );

  const filteredDocs = useMemo(() => {
    if (activeFilter === "all") return docs;
    return docs.filter((d) => d.stage === activeFilter);
  }, [docs, activeFilter]);

  const getApiToken = useCallback(async () => {
    const account = instance.getActiveAccount();
    const tokenResponse = await instance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
    });
    return tokenResponse.accessToken;
  }, [instance]);

  const refreshProjectProfile = useCallback(async () => {
    if (!project?.id || !fetchProjectProfile) return;
    await fetchProjectProfile(project.id);
  }, [project?.id, fetchProjectProfile]);

  // ISO 27001 Cl. 6.1.3(d): control implementation cannot be synced (nor Training opened) while any SoA
  // row has no applicability decision. The server enforces it (SOA_APPLICABILITY_UNSET); this only explains it.
  const unsetSoaControls = useMemo(
    () => listUnsetSoaControls(project?.profile?.statementOfApplicabilityPackage?.rows),
    [project?.profile?.statementOfApplicabilityPackage?.rows],
  );

  const syncFromSoa = useCallback(async () => {
    if (!project?.id) return;
    if (!isLeadAdvisor && !isAdvisor) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/sync-from-soa`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(plainSoaGateMessage(json.error?.message).message || "Sync from SOA failed.");
        return;
      }
      const nextPkg = json.data?.controlImplementationPackage;
      if (nextPkg && updateProjectProfile) {
        const base = project.profile || {};
        updateProjectProfile(project.id, { ...base, controlImplementationPackage: nextPkg });
      }
      await refreshProjectProfile();
      message.success("Control implementation synced from SOA.");
    } catch (err) {
      message.error(err?.message || "Sync from SOA failed.");
    } finally {
      setBusy(false);
    }
  }, [
    project?.id,
    project?.profile,
    isLeadAdvisor,
    isAdvisor,
    getApiToken,
    updateProjectProfile,
    refreshProjectProfile,
  ]);

  /** Guards the first-load auto-sync so it fires at most once per project. */
  const autoSyncProjectRef = useRef(null);
  useEffect(() => {
    if (!project?.id) return;
    if (autoSyncProjectRef.current === project.id) return;
    if (!isLeadAdvisor && !isAdvisor) return;
    if (docs.length > 0) {
      autoSyncProjectRef.current = project.id;
      return;
    }
    // Undecided SoA rows would make the server refuse; wait until every row is decided.
    if (unsetSoaControls.length > 0) return;
    const soaRows = project?.profile?.statementOfApplicabilityPackage?.rows;
    if (!Array.isArray(soaRows) || soaRows.length === 0) return;
    autoSyncProjectRef.current = project.id;
    void syncFromSoa();
  }, [
    project?.id,
    isLeadAdvisor,
    isAdvisor,
    docs.length,
    project?.profile?.statementOfApplicabilityPackage?.rows,
    unsetSoaControls.length,
    syncFromSoa,
  ]);

  const toggleChecklistItem = useCallback(
    async (controlId, stage, itemKey, value) => {
      if (!project?.id) return;
      setBusy(true);
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/checklist`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ stage, itemKey, value }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Could not update checklist.");
          return;
        }
        const nextDoc = json.data?.doc;
        if (nextDoc && updateProjectProfile) {
          const base = project.profile || {};
          const prevPkg = base.controlImplementationPackage || { docs: {} };
          const nextPkg = {
            ...prevPkg,
            docs: { ...(prevPkg.docs || {}), [nextDoc.controlId]: nextDoc },
          };
          updateProjectProfile(project.id, {
            ...base,
            controlImplementationPackage: nextPkg,
          });
        }
        await refreshProjectProfile();
      } catch (err) {
        message.error(err?.message || "Checklist update failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, project?.profile, getApiToken, updateProjectProfile, refreshProjectProfile],
  );

  const submitStage = useCallback(
    async (controlId) => {
      if (!project?.id) return;
      setBusy(true);
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/submit`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Could not submit.");
          return;
        }
        const nextPkg = json.data?.controlImplementationPackage;
        if (nextPkg && updateProjectProfile) {
          const base = project.profile || {};
          updateProjectProfile(project.id, { ...base, controlImplementationPackage: nextPkg });
        }
        await refreshProjectProfile();
        if (json.data?.addedToLibrary) {
          message.success("Document added to Document Library.");
        } else {
          message.success("Stage advanced.");
        }
      } catch (err) {
        message.error(err?.message || "Submit failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, project?.profile, getApiToken, updateProjectProfile, refreshProjectProfile],
  );

  const addCustomChecklistItem = useCallback(
    async (controlId, label) => {
      if (!project?.id || !label) return;
      setBusy(true);
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/add-checklist-item`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ label }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Could not add item.");
          return;
        }
        await refreshProjectProfile();
        message.success("Checklist item added.");
      } catch (err) {
        message.error(err?.message || "Add item failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, getApiToken, refreshProjectProfile],
  );

  const removeChecklistItem = useCallback(
    async (controlId, itemKey) => {
      if (!project?.id || !itemKey) return;
      setBusy(true);
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/remove-checklist-item`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ itemKey }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Could not remove item.");
          return;
        }
        await refreshProjectProfile();
        message.success("Checklist item removed.");
      } catch (err) {
        message.error(err?.message || "Remove item failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, getApiToken, refreshProjectProfile],
  );

  const uploadChecklistAttachment = useCallback(
    async (controlId, stage, itemKey, file) => {
      if (!project?.id || !file) return;
      setBusy(true);
      try {
        const token = await getApiToken();
        const formData = new FormData();
        formData.append("file", file);
        formData.append("stage", stage);
        formData.append("itemKey", itemKey);
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/upload-attachment`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Upload failed.");
          return;
        }
        await refreshProjectProfile();
        message.success("File uploaded.");
      } catch (err) {
        message.error(err?.message || "Upload failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, getApiToken, refreshProjectProfile],
  );

  const removeChecklistAttachment = useCallback(
    async (controlId, stage, itemKey) => {
      if (!project?.id) return;
      setBusy(true);
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/remove-attachment`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ stage, itemKey }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Remove failed.");
          return;
        }
        await refreshProjectProfile();
        message.success("Attachment removed.");
      } catch (err) {
        message.error(err?.message || "Remove failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, getApiToken, refreshProjectProfile],
  );

  const saveCiDraftHtml = useCallback(
    async (controlId, html) => {
      if (!project?.id) return;
      try {
        const token = await getApiToken();
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/control-implementation/${encodeURIComponent(controlId)}/draft`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ html }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Save failed.");
          return false;
        }
        return true;
      } catch (err) {
        message.error(err?.message || "Save failed.");
        return false;
      }
    },
    [project?.id, getApiToken],
  );

  const stageCountsForChips = useMemo(() => {
    const out = { all: docs.length };
    for (const s of STAGE_ORDER) out[s] = summary.byStage[s] || 0;
    return out;
  }, [docs.length, summary]);

  const columns = useMemo(
    () => [
      {
        title: "Document Name",
        dataIndex: "documentName",
        key: "documentName",
        width: 280,
        render: (_, row) => (
          <div>
            <div className="text-sm font-semibold text-[color:var(--color-text-hero)] truncate">
              {stripCompanyPrefixFromDocumentTitle(row.documentName, companyDisplayName) || "Not assigned"}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wide text-[color:var(--color-text-muted)]">
              {controlDocCode(row.controlId)}
            </div>
          </div>
        ),
      },
      {
        title: "Control",
        dataIndex: "controlId",
        key: "controlId",
        width: 96,
        render: (v, row) => (
          <span className="font-mono text-xs font-bold text-[color:var(--color-accent)]">{row.controlIdDisplay || `A.${v}`}</span>
        ),
      },
      {
        title: "Family",
        dataIndex: "family",
        key: "family",
        width: 140,
        render: (v) =>
          v ? (
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                FAMILY_TAG_CLASS[v] || "bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]"
              }`}
            >
              {v}
            </span>
          ) : (
            <span className="text-[10px] text-[color:var(--color-text-muted)]">-</span>
          ),
      },
      {
        title: "Stage",
        dataIndex: "stage",
        key: "stage",
        width: 160,
        render: (s) => {
          const meta = STAGE_META[s] || STAGE_META.drafting;
          return (
            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${meta.pillClass}`}
            >
              {meta.label}
            </span>
          );
        },
      },
      {
        title: "Progress",
        dataIndex: "stage",
        key: "progress",
        width: 160,
        render: (s) => (
          <div className="w-full">
            <Progress
              percent={progressPercentForStage(s)}
              size="small"
              showInfo={false}
            />
          </div>
        ),
      },
      {
        title: "Assignee",
        key: "assignee",
        width: 160,
        render: (_, row) => {
          const meta = STAGE_META[row.stage] || STAGE_META.drafting;
          const member = findAssigneeMember(project?.members, meta.assigneeRole);
          return (
            <div className="text-xs text-[color:var(--color-text-hero)]">
              <div className="font-medium">{member?.name || meta.assigneeLabel}</div>
              <div className="text-[10px] text-[color:var(--color-text-muted)] uppercase tracking-wide">
                {meta.assigneeLabel}
              </div>
            </div>
          );
        },
      },
      {
        title: "Updated",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 110,
        render: (v) => <span className="text-xs text-[color:var(--color-text-muted-dark)]">{formatRelativeDate(v)}</span>,
      },
      {
        title: "Actions",
        key: "actions",
        width: 140,
        render: (_, row) => (
          <div className="flex items-center gap-2">
            <Tooltip title={row.brandedBlobUrl ? "Preview document" : "No document attached"}>
              <Button
                size="small"
                icon={<EyeOutlined />}
                disabled={!row.brandedBlobUrl}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.brandedBlobUrl) setPreviewDoc(row);
                }}
              >
                View
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [project?.members, companyDisplayName],
  );

  const dataSource = useMemo(
    () => filteredDocs.map((d) => ({ ...d, key: d.controlId })),
    [filteredDocs],
  );

  const drawerDoc = useMemo(() => {
    if (!drawerControlId) return null;
    return docs.find((d) => d.controlId === drawerControlId) || null;
  }, [drawerControlId, docs]);

  const closeDrawer = useCallback(() => setDrawerControlId(null), []);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <StageHeader title="Control Implementation" />
          <p className="mt-1 max-w-3xl text-sm font-medium text-[color:var(--color-text-muted-dark)]">
            Manage the lifecycle of policy templates and implementation artefacts across drafting, review,
            publication, training, and implementation.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(isLeadAdvisor || isAdvisor) && (
            <Button
              type="default"
              icon={<ReloadOutlined />}
              loading={busy}
              disabled={unsetSoaControls.length > 0}
              title={unsetSoaControls.length > 0 ? "Decide every Statement of Applicability row first" : undefined}
              onClick={() => void syncFromSoa()}
            >
              Sync from SOA
            </Button>
          )}
          <div className="flex items-stretch gap-2">
            <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3 text-center shadow-sm">
              <div className="text-2xl font-black text-[color:var(--color-text-hero)]">{summary.total}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-text-muted)]">Total Docs</div>
            </div>
            <div className="rounded-xl border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-4 py-3 text-center shadow-sm">
              <div className="text-2xl font-black text-[color:var(--color-accent)]">{summary.inProgress}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-accent)]">In Progress</div>
            </div>
            <div className="rounded-xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-4 py-3 text-center shadow-sm">
              <div className="text-2xl font-black text-[color:var(--color-ok-fg)]">{summary.implemented}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-ok-fg)]">Implemented</div>
            </div>
          </div>
        </div>
      </div>

      <SoaUnsetNotice controls={unsetSoaControls} />

      <PageFilter
        value={activeFilter}
        onChange={setActiveFilter}
        options={STAGE_FILTER_CHIPS.map((chip) => ({
          value: chip.key,
          label: (
            <span className="pl-page-tabs__label">
              {chip.label}
              <Badge count={stageCountsForChips[chip.key] || 0} showZero overflowCount={999} />
            </span>
          ),
        }))}
      />

      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm">
        {dataSource.length === 0 ? (
          <div className="p-10">
            <Empty
              description={
                docs.length === 0
                  ? "No control implementation rows yet. Sync from SOA to start."
                  : "No documents in this filter."
              }
            />
          </div>
        ) : (
          <Table
            size="middle"
            rowKey="key"
            columns={columns}
            dataSource={dataSource}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
            onRow={(row) => ({
              onClick: () => setDrawerControlId(row.controlId),
            })}
            className="cursor-pointer"
          />
        )}
      </div>

      <ControlImplementationLifecycleDrawer
        open={!!drawerDoc}
        doc={drawerDoc}
        user={user}
        projectId={project?.id || ""}
        companyDisplayName={companyDisplayName}
        logoUrl={companyLogoUrl}
        getApiToken={getApiToken}
        onClose={closeDrawer}
        onToggleChecklist={toggleChecklistItem}

        onSubmitStage={submitStage}
        onAddCustomItem={addCustomChecklistItem}
        onRemoveChecklistItem={removeChecklistItem}
        onUploadAttachment={uploadChecklistAttachment}
        onRemoveAttachment={removeChecklistAttachment}
        onSaveDraft={saveCiDraftHtml}
        isLeadAdvisor={isLeadAdvisor}
        isAdvisor={isAdvisor}
        isSpoc={isSpoc}
        isDelegate={isDelegate}
        busy={busy}
        fetchProjectProfile={fetchProjectProfile}
      />

      <Modal
        open={!!previewDoc}
        onCancel={() => setPreviewDoc(null)}
        title={
          previewDoc
            ? stripCompanyPrefixFromDocumentTitle(previewDoc.documentName, companyDisplayName) || "Document"
            : "Document"
        }
        footer={null}
        width={1000}
        destroyOnClose
      >
        {previewDoc?.brandedBlobUrl ? (
          <DelegateDocxEditorPanel
            projectId={project?.id || ""}
            templateRowId={previewDoc.documentRowId || previewDoc.controlId}
            brandedBlobUrl={previewDoc.brandedBlobUrl}
            logoUrl={companyLogoUrl}
            fileLabel={
              stripCompanyPrefixFromDocumentTitle(previewDoc.documentName, companyDisplayName) ||
              previewDoc.documentName ||
              ""
            }
            draftHtmlFromWizard=""
            submittedAt={new Date().toISOString()}
            filledBlobUrl={previewDoc.brandedBlobUrl}
            openBlobUrl={null}
            getApiToken={getApiToken}
            onRefresh={refreshProjectProfile}
          />
        ) : null}
      </Modal>
    </div>
  );
}

/**
 * Inline DOCX editor for CI — saves HTML to the CI draft endpoint (not org-setup delegate draft).
 */
export function CiDocxEditorPanel({ projectId, controlId, brandedBlobUrl, draftHtml, logoUrl, readOnly, getApiToken, onSaveDraft, onRefresh }) {
  const editorRef = React.useRef(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState(null);
  const [initialHtml, setInitialHtml] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadHtml = useCallback(async () => {
    if (!brandedBlobUrl) return;
    setLoading(true);
    setFetchErr(null);
    try {
      if (draftHtml && String(draftHtml).trim()) {
        setInitialHtml(draftHtml);
        setLoading(false);
        return;
      }
      const token = await getApiToken();
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/blob-docx-preview-html?url=${encodeURIComponent(brandedBlobUrl)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const json = await res.json().catch(() => ({}));
      const html = json?.data?.html;
      if (!res.ok || !json.success || typeof html !== "string") {
        setFetchErr("Could not load document for editing.");
        setInitialHtml("");
        setLoading(false);
        return;
      }
      setInitialHtml(html);
    } catch (e) {
      setFetchErr(e?.message || "Load failed.");
      setInitialHtml("");
    } finally {
      setLoading(false);
    }
  }, [brandedBlobUrl, draftHtml, getApiToken]);

  useEffect(() => { void loadHtml(); }, [loadHtml, controlId]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || loading) return;
    if (initialHtml != null) {
      el.innerHTML = initialHtml;
      setDirty(false);
    }
  }, [initialHtml, loading, controlId]);

  const handleSave = async () => {
    if (readOnly) return;
    const el = editorRef.current;
    if (!el || !onSaveDraft) return;
    setSaving(true);
    try {
      const ok = await onSaveDraft(controlId, el.innerHTML);
      if (ok) { setDirty(false); message.success("Draft saved."); }
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex min-h-[280px] items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-sm font-medium text-[color:var(--color-text-muted-dark)]">Loading document…</div>;
  if (fetchErr) return <div className="rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">{fetchErr}</div>;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" disabled={saving} onClick={() => void handleSave()} className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50">Save draft</button>
          {dirty ? <span className="text-xs font-medium text-[color:var(--color-warn-fg)]">Unsaved edits</span> : null}
        </div>
      )}
      <div className="min-h-[360px] flex-1 overflow-y-auto rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-inner">
        <div className="relative">
          {logoUrl ? (
            // Commandment VIII — backlog #39: mammoth strips DOCX headers/footers, so the
            // header-placed client logo never reaches this HTML; overlay it from
            // Company.branding to visually match the real downloaded artefact. Sibling of
            // (not inside) the contentEditable node below, so it is never captured by
            // el.innerHTML and never leaks into a saved draft or the real artefact.
            <img
              src={logoUrl}
              alt="Company Logo"
              className="pointer-events-none absolute left-6 top-4 h-10 w-auto max-w-[180px] object-contain"
            />
          ) : null}
          <div ref={editorRef} contentEditable={!readOnly} suppressContentEditableWarning className={`${DOCX_PREVIEW_BODY_CLASS} min-h-[360px] px-6 py-5 text-[color:var(--color-text-hero)] outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-info-line)]`} onInput={() => setDirty(true)} />
        </div>
      </div>
    </div>
  );
}

/**
 * Right drawer — vertical 6-stage Execution Lifecycle with checklists, file uploads, custom items, and doc editor.
 */
export function ControlImplementationLifecycleDrawer({
  open,
  doc,
  user,
  projectId,
  companyDisplayName,
  logoUrl,
  getApiToken,
  onClose,
  onToggleChecklist,
  onSubmitStage,
  onAddCustomItem,
  onRemoveChecklistItem,
  onUploadAttachment,
  onRemoveAttachment,
  onSaveDraft,
  isLeadAdvisor,
  isAdvisor,
  isSpoc,
  isDelegate,
  busy,
  fetchProjectProfile,
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState("");
  const fileInputRefs = React.useRef({});

  if (!doc) {
    return <Drawer open={open} onClose={onClose} width={520} />;
  }

  const stageIdx = STAGE_ORDER.indexOf(doc.stage);
  const meta = STAGE_META[doc.stage] || STAGE_META.drafting;
  const checklistComplete = isStageChecklistComplete(doc, doc.stage);
  const stageAlreadySubmitted = !!(doc.stageSubmitted && doc.stageSubmitted[doc.stage]);
  const isStageOwner = canRoleEditStageChecklist(user?.role, doc.stage);
  const showSubmitCta = isStageOwner && checklistComplete && !stageAlreadySubmitted;
  const checklistEditable = canRoleEditStageChecklist(user?.role, doc.stage) && !stageAlreadySubmitted;
  const canAddItems = (isAdvisor || isLeadAdvisor || isDelegate || isSpoc) && !stageAlreadySubmitted;

  const ownerStageForEdit =
    (doc.stage === "delegate_review" && isDelegate) ||
    (doc.stage === "spoc_review" && isSpoc) ||
    ((doc.stage === "drafting" || doc.stage === "training" || doc.stage === "implemented") && (isAdvisor || isLeadAdvisor));
  const canEditDocBody = !!doc.brandedBlobUrl && ownerStageForEdit;

  const handleAddItem = () => {
    if (!newItemLabel.trim()) return;
    onAddCustomItem(doc.controlId, newItemLabel.trim());
    setNewItemLabel("");
  };

  const handleFileChange = (stage, itemKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onUploadAttachment(doc.controlId, stage, itemKey, file);
    e.target.value = "";
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      destroyOnClose
      title={
        <div>
          <div className="inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)]">
            {meta.label}
          </div>
          <div className="mt-2 text-xl font-black text-[color:var(--color-text-hero)]">
            {stripCompanyPrefixFromDocumentTitle(doc.documentName, companyDisplayName) || "Not assigned"}
          </div>
          <div className="mt-0.5 text-[11px] font-mono uppercase tracking-wide text-[color:var(--color-text-muted)]">
            {controlDocCode(doc.controlId)} · Control {doc.controlIdDisplay || doc.controlId}
          </div>
          {doc.brandedBlobUrl ? (
            <button type="button" onClick={() => setEditorOpen((v) => !v)} className="mt-2 text-[12px] font-semibold text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)]">
              {editorOpen ? "Hide document" : "View Document"}
            </button>
          ) : null}
        </div>
      }
    >
      <div className="space-y-6">
        {editorOpen && doc.brandedBlobUrl ? (
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-3 shadow-sm">
            <CiDocxEditorPanel
              projectId={projectId}
              controlId={doc.controlId}
              brandedBlobUrl={doc.brandedBlobUrl}
              draftHtml={doc.draftHtml || ""}
              logoUrl={logoUrl}
              readOnly={!canEditDocBody}
              getApiToken={getApiToken}
              onSaveDraft={onSaveDraft}
              onRefresh={fetchProjectProfile ? () => fetchProjectProfile(projectId) : () => {}}
            />
          </div>
        ) : null}

        <div>
          <h4 className="mb-4 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Execution Lifecycle
          </h4>
          <ol className="relative space-y-5">
            {STAGE_ORDER.map((stage, i) => {
              const stageMeta = STAGE_META[stage];
              const isActive = stage === doc.stage;
              const isPast = i < stageIdx;
              const isFuture = i > stageIdx;
              const bag = doc.checklist?.[stage] || {};
              const attachments = doc.checklistAttachments || {};
              const stageItems = allItemsForStage(doc, stage);
              return (
                <li key={stage} className={`flex gap-3 ${isFuture ? "opacity-50" : ""}`}>
                  <div className="flex flex-col items-center">
                    <span className={`mt-1 h-3 w-3 rounded-full border-2 ${isActive ? "border-[color:var(--color-ok-fg)] bg-[color:var(--color-ok-fg)]" : isPast ? "border-[color:var(--color-border-strong)] bg-[color:var(--color-text-muted)]" : "border-[color:var(--color-border-strong)] bg-[color:var(--color-bg-white)]"}`} />
                    {i < STAGE_ORDER.length - 1 && <span className="my-1 h-full w-px flex-1 bg-[color:var(--color-border-strong)]" />}
                  </div>
                  <div className="flex-1 pb-2">
                    {/* Backlog #38: each lifecycle stage is a CollapsiblePane section so any
                        stage's checklist can be reviewed on demand, not only the active one.
                        pageId is scoped per doc.controlId so collapse state never leaks across
                        different control documents' drawers. defaultExpanded mirrors the prior
                        hardcoded isActive-only reveal; no gating/editability logic is touched. */}
                    <CollapsiblePane
                      id={stage}
                      pageId={`control-implementation-${doc.controlId}`}
                      defaultExpanded={isActive}
                      title={<span className={`text-[11px] font-black uppercase tracking-widest ${isActive ? "text-[color:var(--color-text-hero)]" : isPast ? "text-[color:var(--color-text-muted)]" : "text-[color:var(--color-text-muted)]"}`}>{stageMeta.label}</span>}
                      summary={`Owner: ${stageMeta.assigneeLabel}`}
                      headerRight={isActive ? <span className="inline-flex items-center rounded-full border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[color:var(--color-ok-fg)]">In progress</span> : null}
                    >
                    {isActive && (
                      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-3">
                        <div className="mb-2 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Checklist</div>
                        <div className="space-y-3">
                          {stageItems.map((item) => {
                            const checked = bag[item.key] === true;
                            const attachKey = `${stage}::${item.key}`;
                            const att = attachments[attachKey];
                            return (
                              <div key={item.key} className="space-y-1">
                                <div className="flex items-start gap-2">
                                  <Checkbox checked={checked} disabled={!checklistEditable || busy} onChange={(e) => onToggleChecklist(doc.controlId, stage, item.key, e.target.checked)} />
                                  <div className="flex-1">
                                    <div className={`text-[12px] ${checked ? "text-[color:var(--color-text-muted)] line-through" : "text-[color:var(--color-text-hero)]"}`}>{item.label}</div>
                                    <div className="text-[10px] text-[color:var(--color-text-muted)]">Assignee: {item.assignee}</div>
                                  </div>
                                  {checklistEditable && !stageAlreadySubmitted && (
                                    <Tooltip title="Remove item">
                                      <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => onRemoveChecklistItem(doc.controlId, item.key)}
                                        className="ml-1 mt-0.5 text-[11px] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] disabled:opacity-50 transition-colors"
                                        aria-label="Remove checklist item"
                                      >
                                        ✕
                                      </button>
                                    </Tooltip>
                                  )}
                                </div>
                                {checked && (
                                  <div className="ml-6">
                                    {att ? (
                                      <div className="flex items-center gap-2 text-[10px]">
                                        <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--color-ok-fg)]" />
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            const win = preopenWindow();
                                            let target = att.blobUrl;
                                            try {
                                              const token = await getApiToken();
                                              const r = await fetch(
                                                `${apiConfig.baseUrl}/api/v1/blob-sas?url=${encodeURIComponent(att.blobUrl)}`,
                                                { headers: { Authorization: `Bearer ${token}` } },
                                              );
                                              const j = await r.json().catch(() => ({}));
                                              if (j?.data?.url) target = j.data.url;
                                            } catch { /* fall back to the stored URL */ }
                                            if (!openServerUrl(target, win)) reportOpenFailure(message, target);
                                          }}
                                          className="font-medium text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] truncate max-w-[180px] text-left"
                                          title={att.fileName}
                                        >
                                          {att.fileName}
                                        </button>
                                        {!stageAlreadySubmitted && checklistEditable && (
                                          <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => onRemoveAttachment(doc.controlId, stage, item.key)}
                                            className="text-[10px] font-semibold text-[color:var(--color-danger-fg)] hover:text-[color:var(--color-danger-fg)] disabled:opacity-50"
                                          >
                                            Remove
                                          </button>
                                        )}
                                      </div>
                                    ) : checklistEditable ? (
                                      <div>
                                        <input
                                          type="file"
                                          className="hidden"
                                          ref={(el) => { fileInputRefs.current[attachKey] = el; }}
                                          onChange={(e) => handleFileChange(stage, item.key, e)}
                                        />
                                        <button
                                          type="button"
                                          disabled={busy}
                                          onClick={() => fileInputRefs.current[attachKey]?.click()}
                                          className="text-[10px] font-semibold text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] disabled:opacity-50"
                                        >
                                          + Attach file
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {canAddItems && (
                          <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--color-border)] pt-3">
                            <input
                              type="text"
                              value={newItemLabel}
                              onChange={(e) => setNewItemLabel(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleAddItem(); }}
                              placeholder="Add a checklist item…"
                              className="flex-1 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-1.5 text-xs text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                            />
                            <Button size="small" type="dashed" disabled={!newItemLabel.trim() || busy} onClick={handleAddItem}>Add</Button>
                          </div>
                        )}
                        {stageAlreadySubmitted ? (
                          <div className="mt-3 text-[10px] font-semibold text-[color:var(--color-ok-fg)]">Submitted - advanced to next stage.</div>
                        ) : null}
                        {showSubmitCta ? (
                          <div className="mt-4 flex justify-end">
                            <Button type="primary" loading={busy} onClick={() => void onSubmitStage(doc.controlId)} className="bg-[color:var(--color-ok-fg)] hover:opacity-80 border-[color:var(--color-ok-fg)]">
                              {meta.advanceCtaLabel || "Submit"}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    )}
                    {isPast && stageItems.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {stageItems.map((item) => {
                          const attachKey = `${stage}::${item.key}`;
                          const att = (doc.checklistAttachments || {})[attachKey];
                          return (
                            <div key={item.key} className="flex items-start gap-2">
                              <span className="mt-1 inline-block h-2 w-2 rounded-sm bg-[color:var(--color-ok-fg)]" />
                              <div>
                                <div className="text-[11px] text-[color:var(--color-text-muted)] line-through">{item.label}</div>
                                {att && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const win = preopenWindow();
                                      let target = att.blobUrl;
                                      try {
                                        const token = await getApiToken();
                                        const r = await fetch(
                                          `${apiConfig.baseUrl}/api/v1/blob-sas?url=${encodeURIComponent(att.blobUrl)}`,
                                          { headers: { Authorization: `Bearer ${token}` } },
                                        );
                                        const j = await r.json().catch(() => ({}));
                                        if (j?.data?.url) target = j.data.url;
                                      } catch { /* fall back to the stored URL */ }
                                      if (!openServerUrl(target, win)) reportOpenFailure(message, target);
                                    }}
                                    className="text-[9px] text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] truncate max-w-[180px] block text-left"
                                    title={att.fileName}
                                  >
                                    {att.fileName}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </CollapsiblePane>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </Drawer>
  );
}

export default ControlImplementation;
